import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { BulkWriteOffOrder, BulkWriteOffSide } from '@billflow/shared';
import { Badge } from '@/components/ui/badge';
import { ESTIMATE_STATUS_BADGE, PO_STATUS_BADGE } from '@/lib/badges';
import { formatCents, formatDate, cn } from '@/lib/utils';

interface BulkWriteOffTableProps {
  side: BulkWriteOffSide;
  orders: BulkWriteOffOrder[];
  selectedIds: Set<number>;
  onToggleOne: (id: number) => void;
  onToggleAll: () => void;
}

function statusBadge(side: BulkWriteOffSide, status: string) {
  const map = side === 'ar' ? ESTIMATE_STATUS_BADGE : PO_STATUS_BADGE;
  const entry = (
    map as Record<string, { variant: 'gray' | 'blue' | 'amber' | 'green' | 'red'; label: string }>
  )[status];
  if (!entry) return <Badge variant="gray">{status}</Badge>;
  return <Badge variant={entry.variant}>{entry.label}</Badge>;
}

/**
 * Selectable results table for the bulk write-off tool. Header checkbox is
 * tri-state (checked / indeterminate / unchecked) based on how many of the
 * loaded rows are currently selected.
 */
export function BulkWriteOffTable({
  side,
  orders,
  selectedIds,
  onToggleOne,
  onToggleAll,
}: BulkWriteOffTableProps) {
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const numberHeader = side === 'ar' ? 'Invoice #' : 'PO #';
  const partyHeader = side === 'ar' ? 'Customer' : 'Supplier';
  const detailBase = side === 'ar' ? '/estimates/' : '/purchase-orders/';

  const allSelected = orders.length > 0 && orders.every((o) => selectedIds.has(o.id));
  const someSelected = orders.some((o) => selectedIds.has(o.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  return (
    <div className="rounded-lg border overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="w-10 px-4 py-3">
              <input
                ref={headerCheckboxRef}
                type="checkbox"
                checked={allSelected}
                onChange={onToggleAll}
                disabled={orders.length === 0}
                aria-label="Select all"
                className="h-4 w-4 rounded border-input"
              />
            </th>
            <th className="px-4 py-3 text-left font-medium text-muted-foreground">
              {numberHeader}
            </th>
            <th className="px-4 py-3 text-left font-medium text-muted-foreground">{partyHeader}</th>
            <th className="px-4 py-3 text-left font-medium text-muted-foreground">Date</th>
            <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
            <th className="px-4 py-3 text-right font-medium text-muted-foreground">Remaining</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {orders.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                No eligible {side === 'ar' ? 'invoices' : 'purchase orders'} in range.
              </td>
            </tr>
          ) : (
            orders.map((o) => {
              const checked = selectedIds.has(o.id);
              return (
                <tr
                  key={o.id}
                  className={cn(
                    'transition-colors',
                    checked
                      ? 'hover:bg-muted/30'
                      : 'bg-muted/20 text-muted-foreground hover:bg-muted/30',
                  )}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggleOne(o.id)}
                      aria-label={`Select ${o.number}`}
                      className="h-4 w-4 rounded border-input"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`${detailBase}${o.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline underline-offset-4"
                    >
                      {o.number}
                    </Link>
                  </td>
                  <td className="px-4 py-3">{o.party}</td>
                  <td className="px-4 py-3">{formatDate(o.date)}</td>
                  <td className="px-4 py-3">{statusBadge(side, o.status)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium">
                    {formatCents(o.remaining)}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
